import { useCallback, useEffect, useState } from 'react';
import {
  getAvailableDeviceTypes,
  requestMediaPermissionsDetailed,
  type MediaPermissionResult,
} from '../services/webrtc/mediaDevices';

export type MediaPermissionStatus = 'prompt' | 'granted' | 'denied' | 'no-devices';

export interface MediaPermissionState {
  status: MediaPermissionStatus;
  audioGranted: boolean;
  videoGranted: boolean;
  hasAudioDevice: boolean;
  hasVideoDevice: boolean;
  request: () => Promise<void>;
}

/**
 * Tracks camera and microphone permission state and requests on demand.
 * Handles cases where audio or video devices may not exist.
 */
export function useMediaPermissions(
  loadDevices: () => Promise<void>
): MediaPermissionState {
  const [status, setStatus] = useState<MediaPermissionStatus>('prompt');
  const [audioGranted, setAudioGranted] = useState(false);
  const [videoGranted, setVideoGranted] = useState(false);
  const [hasAudioDevice, setHasAudioDevice] = useState(true);
  const [hasVideoDevice, setHasVideoDevice] = useState(true);

  useEffect(() => {
    let isActive = true;

    const checkPermissions = async () => {
      const deviceTypes = await getAvailableDeviceTypes();

      if (!isActive) return;

      setHasAudioDevice(deviceTypes.hasAudio);
      setHasVideoDevice(deviceTypes.hasVideo);

      if (!deviceTypes.hasAudio && !deviceTypes.hasVideo) {
        setStatus('no-devices');
        return;
      }

      if (!navigator.permissions?.query) {
        return;
      }

      try {
        const results = await queryPermissionStates(deviceTypes);
        if (!isActive) return;

        const audioState = deviceTypes.hasAudio ? results.microphone : null;
        const videoState = deviceTypes.hasVideo ? results.camera : null;

        setAudioGranted(audioState === 'granted');
        setVideoGranted(videoState === 'granted');

        const newStatus = deriveStatus(audioState, videoState, deviceTypes);
        setStatus(newStatus);

        if (newStatus === 'granted') {
          await loadDevices();
        }
      } catch {
        // Fall back to prompt state
      }
    };

    void checkPermissions();
    return () => {
      isActive = false;
    };
  }, [loadDevices]);

  const request = useCallback(async () => {
    setStatus('prompt');
    const result = await requestMediaPermissionsDetailed();

    setAudioGranted(result.audio);
    setVideoGranted(result.video);
    setHasAudioDevice(result.hasAudioDevice);
    setHasVideoDevice(result.hasVideoDevice);
    setStatus(deriveStatusFromResult(result));

    if (result.audio || result.video) {
      await loadDevices();
    }
  }, [loadDevices]);

  return {
    status,
    audioGranted,
    videoGranted,
    hasAudioDevice,
    hasVideoDevice,
    request,
  };
}

function deriveStatus(
  audioState: PermissionState | null,
  videoState: PermissionState | null,
  deviceTypes: { hasAudio: boolean; hasVideo: boolean }
): MediaPermissionStatus {
  const audioOk = !deviceTypes.hasAudio || audioState === 'granted';
  const videoOk = !deviceTypes.hasVideo || videoState === 'granted';
  const audioDenied = deviceTypes.hasAudio && audioState === 'denied';
  const videoDenied = deviceTypes.hasVideo && videoState === 'denied';

  if (audioOk && videoOk) {
    return 'granted';
  }

  if (audioDenied || videoDenied) {
    return 'denied';
  }

  return 'prompt';
}

function deriveStatusFromResult(result: MediaPermissionResult): MediaPermissionStatus {
  if (!result.hasAudioDevice && !result.hasVideoDevice) {
    return 'no-devices';
  }

  const audioOk = !result.hasAudioDevice || result.audio;
  const videoOk = !result.hasVideoDevice || result.video;

  if (audioOk && videoOk) {
    return 'granted';
  }

  return 'denied';
}

async function queryPermissionStates(deviceTypes: {
  hasAudio: boolean;
  hasVideo: boolean;
}): Promise<{ camera: PermissionState | null; microphone: PermissionState | null }> {
  let camera: PermissionState | null = null;
  let microphone: PermissionState | null = null;

  if (deviceTypes.hasVideo) {
    try {
      const result = await navigator.permissions.query({
        name: 'camera' as unknown as PermissionName,
      });
      camera = result.state;
    } catch {
      // Permission query not supported for camera
    }
  }

  if (deviceTypes.hasAudio) {
    try {
      const result = await navigator.permissions.query({
        name: 'microphone' as unknown as PermissionName,
      });
      microphone = result.state;
    } catch {
      // Permission query not supported for microphone
    }
  }

  return { camera, microphone };
}
